/**
 * ============================================================================
 * Iron Meridian - Player Panel Component
 * ============================================================================
 * 
 * Shows player status, empire overview, and game controls.
 * Premium design with clear visual hierarchy.
 */

import { useState } from 'react'
import { createPortal } from 'react-dom'

export default function PlayerPanel({ gameState, playerCountry, onNewGame, onReturnToMenu }) {
  const [showNewGameModal, setShowNewGameModal] = useState(false)

  return (
    <div className="p-5 border-b border-war-room-600/30">
      {/* Header with branding */}
      <div className="flex items-center justify-between mb-5">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-war-room-accent to-blue-700 flex items-center justify-center shadow-lg">
            <span className="text-xl">🌍</span>
          </div>
          <div>
            <h1 className="text-lg font-bold text-white tracking-tight">Iron Meridian</h1>
            <p className="text-xs text-gray-500">Global Strategy</p>
          </div>
        </div>
        <div className="flex gap-1.5">
          {onReturnToMenu && (
            <button
              onClick={onReturnToMenu}
              className="text-xs px-3 py-1.5 bg-war-room-700/80 hover:bg-war-room-600 rounded-lg transition-all hover:scale-105 border border-war-room-600/50"
              title="Return to Main Menu"
            >
              Menu
            </button>
          )}
          <button
            onClick={() => setShowNewGameModal(true)}
            className="text-xs px-3 py-1.5 bg-war-room-700/80 hover:bg-war-room-600 rounded-lg transition-all hover:scale-105 border border-war-room-600/50"
            title="Start New Game"
          >
            New Game
          </button>
        </div>
      </div>

      {/* Player Empire Card */}
      {playerCountry && (
        <div className="bg-gradient-to-br from-war-room-700/50 to-war-room-800/50 rounded-xl p-4 mb-4 border border-war-room-600/30 shadow-lg">
          <div className="flex items-center gap-4">
            <div 
              className="w-14 h-14 rounded-xl flex items-center justify-center text-xl font-bold shadow-md ring-2 ring-white/10"
              style={{ backgroundColor: playerCountry.color }}
            >
              {playerCountry.id?.slice(0, 2)}
            </div>
            <div className="flex-1 min-w-0">
              <div className="font-bold text-lg text-white truncate">{playerCountry.name}</div>
              <div className="text-xs text-gray-400 flex items-center gap-1.5">
                <span className="text-yellow-400">👑</span>
                Your Empire
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Quick Stats Grid */}
      {playerCountry && (
        <div className="grid grid-cols-2 gap-2">
          <StatCard 
            label="Military" 
            value={playerCountry.military} 
            icon="⚔️" 
            color="red"
            trend={playerCountry.militaryTrend}
          />
          <StatCard 
            label="Economy" 
            value={playerCountry.economy} 
            icon="💰" 
            color="yellow"
            trend={playerCountry.economyTrend}
          />
          <StatCard 
            label="Technology" 
            value={playerCountry.technology} 
            icon="🔬" 
            color="blue"
            trend={playerCountry.technologyTrend}
          />
          <StatCard 
            label="Stability" 
            value={playerCountry.stability || 80} 
            icon="🏛️" 
            color="green"
          />
        </div>
      )}
      
      {/* Victory Progress Indicator */}
      {gameState?.victoryProgress && (
        <div className="mt-3">
          <VictoryProgress progress={gameState.victoryProgress} />
        </div>
      )}
      
      {/* Endgame Pressure Warning */}
      {gameState?.endgamePressure && gameState.endgamePressure.phase !== 'early' && (
        <div className="mt-2">
          <EndgamePressureIndicator pressure={gameState.endgamePressure} />
        </div>
      )}

      {/* New Game Modal - Rendered via Portal to escape stacking context */}
      {showNewGameModal && createPortal(
        <NewGameModal 
          onClose={() => setShowNewGameModal(false)}
          onNewGame={(country) => {
            onNewGame(country)
            setShowNewGameModal(false)
          }}
        />,
        document.body
      )}
    </div>
  )
}

function StatCard({ label, value, icon, color, trend }) {
  const colorClasses = {
    red: { text: 'text-red-400', bg: 'bg-red-500/10', border: 'border-red-500/20' },
    yellow: { text: 'text-yellow-400', bg: 'bg-yellow-500/10', border: 'border-yellow-500/20' },
    blue: { text: 'text-blue-400', bg: 'bg-blue-500/10', border: 'border-blue-500/20' },
    green: { text: 'text-green-400', bg: 'bg-green-500/10', border: 'border-green-500/20' }
  }
  const colors = colorClasses[color] || colorClasses.blue

  return (
    <div className={`${colors.bg} ${colors.border} border rounded-xl px-3 py-2.5 flex items-center gap-2.5 transition-all hover:scale-[1.02]`}>
      <span className="text-lg">{icon}</span>
      <div className="flex-1 min-w-0">
        <div className={`font-bold text-lg tabular-nums ${colors.text}`}>{value}</div>
        <div className="text-xs text-gray-500 truncate">{label}</div>
      </div>
      {trend !== undefined && (
        <span className={`text-xs ${trend > 0 ? 'text-green-400' : trend < 0 ? 'text-red-400' : 'text-gray-500'}`}>
          {trend > 0 ? '↑' : trend < 0 ? '↓' : '−'}
        </span>
      )}
    </div>
  )
}

function VictoryProgress({ progress }) {
  // progress.axes contains { military, economic, technology, diplomatic, stability }
  // Each has { level: 'dominant'|'strong'|'moderate'|'weak' }
  
  const axes = progress?.axes || {}
  const dominantCount = Object.values(axes).filter(a => a.level === 'dominant').length
  const strongCount = Object.values(axes).filter(a => a.level === 'strong').length
  
  // Qualitative description (no exact numbers)
  let description = 'Building strength'
  let barColor = 'bg-gray-600'
  let barWidth = '20%'
  
  if (dominantCount >= 3) {
    description = 'Approaching victory'
    barColor = 'bg-yellow-500'
    barWidth = '90%'
  } else if (dominantCount >= 1) {
    description = 'Significant power'
    barColor = 'bg-blue-500'
    barWidth = '60%'
  } else if (strongCount >= 2) {
    description = 'Growing influence'
    barColor = 'bg-green-500'
    barWidth = '40%'
  }
  
  return (
    <div className="bg-war-room-700/30 rounded-xl p-3 border border-war-room-600/30">
      <div className="flex items-center justify-between mb-2">
        <span className="text-xs text-gray-400 font-medium">Victory Path</span>
        <span className="text-xs text-gray-300">{description}</span>
      </div>
      <div className="h-1.5 bg-war-room-800 rounded-full overflow-hidden">
        <div 
          className={`h-full ${barColor} transition-all duration-1000 ease-out rounded-full`}
          style={{ width: barWidth }}
        />
      </div>
      <div className="flex gap-1.5 mt-2">
        {Object.entries(axes).map(([name, data]) => (
          <AxisDot key={name} name={name} level={data.level} />
        ))}
      </div>
    </div>
  )
}

function AxisDot({ name, level }) {
  const icons = { military: '⚔️', economic: '💰', technology: '🔬', diplomatic: '🤝', stability: '🏛️' }
  const colors = {
    dominant: 'bg-yellow-500 ring-2 ring-yellow-400/50',
    strong: 'bg-blue-500',
    moderate: 'bg-green-500',
    weak: 'bg-gray-600'
  }
  
  return (
    <div 
      className={`flex-1 h-2 rounded-full ${colors[level]} transition-all`}
      title={`${name}: ${level}`}
    />
  )
}

function EndgamePressureIndicator({ pressure }) {
  const phaseColors = {
    mid: { bg: 'bg-blue-900/50', border: 'border-blue-500/30', text: 'text-blue-300', icon: '⚡' },
    late: { bg: 'bg-yellow-900/50', border: 'border-yellow-500/40', text: 'text-yellow-300', icon: '⚠️' },
    endgame: { bg: 'bg-orange-900/50', border: 'border-orange-500/50', text: 'text-orange-300', icon: '🔥' },
    critical: { bg: 'bg-red-900/50', border: 'border-red-500/60', text: 'text-red-300', icon: '💥' }
  }
  
  const style = phaseColors[pressure.phase] || phaseColors.mid
  
  return (
    <div className={`${style.bg} ${style.border} border rounded-xl p-2.5 flex items-center gap-2`}>
      <span className="text-lg">{style.icon}</span>
      <div className="flex-1 min-w-0">
        <div className={`text-xs font-medium ${style.text}`}>{pressure.description}</div>
        {pressure.coalitionTrigger && (
          <div className="text-xs text-gray-400 mt-0.5">⚠️ Powers aligning against leader</div>
        )}
      </div>
    </div>
  )
}

function NewGameModal({ onClose, onNewGame }) {
  const countries = [
    { id: 'USA', name: 'United States', flag: '🇺🇸', difficulty: 'Easy' },
    { id: 'CHN', name: 'China', flag: '🇨🇳', difficulty: 'Medium' },
    { id: 'RUS', name: 'Russia', flag: '🇷🇺', difficulty: 'Medium' },
    { id: 'DEU', name: 'Germany', flag: '🇩🇪', difficulty: 'Hard' },
    { id: 'GBR', name: 'United Kingdom', flag: '🇬🇧', difficulty: 'Medium' },
    { id: 'JPN', name: 'Japan', flag: '🇯🇵', difficulty: 'Hard' },
  ]

  return (
    <div className="fixed inset-0 bg-black/80 backdrop-blur-sm flex items-center justify-center z-50 animate-fade-in">
      <div className="bg-gradient-to-b from-war-room-800 to-war-room-900 rounded-2xl p-6 w-[420px] shadow-2xl border border-war-room-600/50">
        <div className="text-center mb-6">
          <div className="w-16 h-16 mx-auto mb-4 rounded-2xl bg-gradient-to-br from-war-room-accent to-blue-700 flex items-center justify-center shadow-lg">
            <span className="text-3xl">🌍</span>
          </div>
          <h2 className="text-2xl font-bold text-white">New Campaign</h2>
          <p className="text-gray-400 text-sm mt-1">Select your starting nation</p>
        </div>
        
        <div className="grid grid-cols-2 gap-2 mb-5">
          {countries.map(country => (
            <button
              key={country.id}
              onClick={() => onNewGame(country.id)}
              className="flex items-center gap-3 p-3.5 bg-war-room-700/50 hover:bg-war-room-600/50 rounded-xl transition-all border border-war-room-600/30 hover:border-war-room-500/50 hover:scale-[1.02] text-left group"
            >
              <span className="text-3xl group-hover:scale-110 transition-transform">{country.flag}</span>
              <div>
                <div className="font-medium text-sm text-white">{country.name}</div>
                <div className={`text-xs ${country.difficulty === 'Easy' ? 'text-green-400' : country.difficulty === 'Hard' ? 'text-red-400' : 'text-yellow-400'}`}>
                  {country.difficulty}
                </div>
              </div>
            </button>
          ))}
        </div>
        
        <button
          onClick={onClose}
          className="w-full py-3 bg-war-room-700/50 hover:bg-war-room-600 rounded-xl transition-colors text-sm text-gray-300 hover:text-white border border-war-room-600/30"
        >
          Cancel
        </button>
      </div>
    </div>
  )
}
